import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import dayjs from 'dayjs';
import { useState, useEffect, useMemo } from 'react';
import { Redirect, Switch, Route } from 'react-router-dom';
import { storeUserUiLocale } from 'api/user-api';
import * as clientEnv from 'libs/client-env';
import { Authenticated } from 'components/Authenticated';
import LoginScreen from 'screens/LoginScreen';
import SignUpScreen from 'screens/SignUpScreen';
import PaymentScreen from 'screens/PaymentScreen';
import ResetPasswordScreen from 'screens/ResetPasswordScreen';
import DashboardScreen from 'screens/DashboardScreen';
import { setCurrentUiLocale, getCurrentUiLocale } from 'libs/locales/ui';
import { getBrowserStatus } from 'libs/browser';
import VersionMismatchOverlay from 'components/VersionMismatchOverlay';
import { App, ConfigProvider, Modal, message, Button, } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { txt } from 'libs/i18n';
import { loggerService } from 'libs/logger';
import { useAppTheme } from 'hooks/useAppTheme';
import { AppContext, screenBreakpoints, } from './app-context';
import { beeyTheme } from '../ant-config';
// NOTE: Run startup cleanup tasks
const migrationReport = clientEnv.migrateClientEnv();
if (migrationReport !== null) {
    loggerService.getRoot().info(migrationReport.messages.join('\n'));
}
const browserStatus = getBrowserStatus();
const isLoginEnabled = browserStatus.access === 'ok' || browserStatus.access === 'warn';
const inferSizeLabel = (windowWidth) => {
    if (windowWidth < screenBreakpoints.sm) {
        return 'xs';
    }
    if (windowWidth < screenBreakpoints.md) {
        return 'sm';
    }
    if (windowWidth < screenBreakpoints.lg) {
        return 'md';
    }
    if (windowWidth < screenBreakpoints.xl) {
        return 'lg';
    }
    if (windowWidth < screenBreakpoints.xxl) {
        return 'xl';
    }
    return 'xxl';
};
const BeeyApp = () => {
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
        label: inferSizeLabel(window.innerWidth),
    });
    const [uiLocale, setUILocale] = useState(() => getCurrentUiLocale());
    const [session, setSession] = useState('continue');
    const [versionMatch, setVersionMatch] = useState('perfect');
    const [theme, setTheme] = useState(beeyTheme);
    const [messageApi, contextHolder] = message.useMessage();
    const [showGlobalError, setShowGlobalError] = useState(false);
    const { changeTheme } = useAppTheme();
    useEffect(() => {
        const handleResize = () => setScreenSize({
            width: window.innerWidth,
            height: window.innerHeight,
            label: inferSizeLabel(window.innerWidth),
        });
        const handleOffline = () => {
            void messageApi.error({
                content: txt('offline'),
                duration: 0,
            });
        };
        const handleOnline = () => {
            void messageApi.destroy();
        };
        const handleUnhandledRejection = (event) => {
            var _a;
            const error = event.reason;
            event.preventDefault();
            if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) >= 500) {
                setShowGlobalError(true);
            }
            else {
                global.logger.info('Unhandled promise rejection:', error);
            }
        };
        changeTheme(setTheme);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        window.addEventListener('resize', handleResize);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);
    const changeUiLocale = (locale) => {
        setCurrentUiLocale(locale);
        setUILocale(locale);
        dayjs.locale(locale.lang);
    };
    const updateSession = (openedSession) => {
        setSession(openedSession);
        const hasApaClaim = openedSession.login.hasClaim('dataTheme:apa');
        changeTheme(setTheme, hasApaClaim);
        clientEnv.setUserEmail(openedSession.login.user.email);
        clientEnv.setUserId(openedSession.login.user.id.toString());
        if (openedSession.login.user.uiLocale.code !== uiLocale.code) {
            void storeUserUiLocale(openedSession.connection, uiLocale);
        }
    };
    const clearSession = () => {
        setSession('closed');
    };
    const triggerVersionMismatch = (mismatch) => {
        if (versionMatch !== 'incompatible' && mismatch !== versionMatch) {
            setVersionMatch(mismatch);
        }
    };
    const appContextValue = useMemo(() => ({
        screenSize,
        uiLocale,
        browserStatus,
        versionMatch,
        triggerVersionMismatch,
        isLoginEnabled,
        changeUiLocale,
        session,
        updateSession,
        clearSession,
    }), [
        screenSize,
        uiLocale,
        browserStatus,
        versionMatch,
        triggerVersionMismatch,
        isLoginEnabled,
        changeUiLocale,
        session,
        updateSession,
        clearSession,
    ]);
    const handleLogout = async () => {
        if (session !== 'continue' && session !== 'closed') {
            const logoutSuccess = await session.explicitLogout();
            if (!logoutSuccess) {
                void messageApi.error(txt('logoutFailed'));
            }
            setShowGlobalError(false);
        }
    };
    return (_jsx(ConfigProvider, { theme: theme, children: _jsxs(App, { children: [contextHolder, showGlobalError && (_jsx(Modal, { title: _jsx(WarningOutlined, {}), closable: false, open: true, footer: (_jsxs(_Fragment, { children: [_jsx(Button, { type: "primary", size: "small", danger: true, onClick: () => { void handleLogout(); }, children: txt('logout') }), _jsx(Button, { type: "primary", size: "small", danger: true, onClick: () => setShowGlobalError(false), children: txt('ignore') })] })), children: txt('globalErrorWarning') })), _jsx(AppContext.Provider, { value: appContextValue, children: _jsx(VersionMismatchOverlay, { children: _jsxs(Switch, { children: [_jsx(Redirect, { exact: true, from: "/", to: "/projects" }), _jsx(Redirect, { exact: true, from: "/documents", to: "/projects" }), _jsx(Redirect, { exact: true, from: "/docs", to: "/projects" }), _jsx(Redirect, { exact: true, from: "/projs", to: "/projects" }), _jsx(Route, { exact: true, path: "/signup", component: SignUpScreen }), _jsx(Route, { exact: true, path: "/signup/createpassword", component: ResetPasswordScreen }), _jsx(Route, { exact: true, path: "/login", component: LoginScreen }), _jsx(Route, { exact: true, path: "/login/resetpassword", component: ResetPasswordScreen }), _jsx(Route, { exact: true, path: "/login/setpassword", component: ResetPasswordScreen }), _jsxs(Authenticated, { children: [_jsx(Route, { exact: true, path: "/payment", component: PaymentScreen }), _jsx(Route, { path: "/:page", component: DashboardScreen })] })] }) }) })] }) }));
};
export default BeeyApp;
